import { Button } from "components/ui/Button";
import { cn } from "lib/utils";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { FC, ReactNode } from "react";

export type CollapsiblePanelProps = {
  /** パネルタイトル (例: 返信リスト / メールスレッド / メール返信フォーム) */
  title: string;
  /** タイトル左のアイコン (任意) */
  icon?: ReactNode;
  /** 折り畳み中なら true */
  collapsed: boolean;
  /** 開閉トグル (折り畳み中の rail クリック / 展開中のボタン) */
  onToggle: () => void;
  /**
   * false のとき展開ヘッダに折り畳みボタンを表示しない.
   * 常時表示するカラム (メールスレッド) や、選択操作で畳むカラム (返信リスト) に使う.
   * 既定 true.
   */
  collapsible?: boolean;
  /** 展開時の幅クラス (flex basis / min-w など). 未指定なら flex-1 */
  widthClassName?: string;
  /** ヘッダ右端に置く追加アクション (任意) */
  headerActions?: ReactNode;
  /** パネル本文 */
  children: ReactNode;
};

/**
 * 開閉できるカラムパネル. 展開時はヘッダ + スクロール本文、折り畳み時は
 * クリックで展開できる細い縦 rail を表示する. 3 カラム開閉レイアウトの基本部品.
 * @param {CollapsiblePanelProps} props パネルの状態・ハンドラ・内容
 * @return {JSX.Element} パネル要素
 */
export const CollapsiblePanel: FC<CollapsiblePanelProps> = ({
  title,
  icon,
  collapsed,
  onToggle,
  collapsible = true,
  widthClassName,
  headerActions,
  children,
}) => {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        aria-label={`${title}を開く`}
        title={`${title}を開く`}
        className="shrink-0 w-9 bg-white rounded-lg border border-slate-200 flex flex-col items-center gap-2 py-3 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      >
        <PanelLeftOpen size={16} aria-hidden />
        <span className="[writing-mode:vertical-rl] text-xs tracking-wide">
          {title}
        </span>
      </button>
    );
  }

  return (
    <section
      className={cn(
        "min-h-0 min-w-0 bg-white rounded-lg border border-slate-200 flex flex-col overflow-hidden",
        widthClassName ?? "flex-1",
      )}
    >
      <header className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 shrink-0">
        {icon}
        <span className="text-sm font-semibold text-slate-700 truncate">
          {title}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {headerActions}
          {collapsible && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              shape="square"
              onClick={onToggle}
              aria-expanded={true}
              aria-label={`${title}を折り畳む`}
              title={`${title}を折り畳む`}
            >
              <PanelLeftClose size={14} aria-hidden />
            </Button>
          )}
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-auto">{children}</div>
    </section>
  );
};
